import {useEffect, useRef } from "react";
import {ArrowBigLeft, ArrowBigRight, Circle, CircleDot} from "lucide-react"
import "./ImageSlider.scss"

type ImageSliderProps = {
    images: { url: string; alt: string; desc?: string }[];
    imageIndex: number;
    setImageIndex: React.Dispatch<React.SetStateAction<number>>;
};

export function ImageSlider({ images, imageIndex, setImageIndex }: ImageSliderProps) {
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    function resetTimer() {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setImageIndex(index => (index === images.length - 1 ? 0 : index + 1));
        }, 555000);
    }

    function showNextImage() {
        setImageIndex(index => (index === images.length - 1 ? 0 : index + 1));
        resetTimer();
    }

    function showPrevImage() {
        setImageIndex(index => (index === 0 ? images.length - 1 : index - 1));
        resetTimer();
    }

    useEffect(() => {
        resetTimer(); // ✅ Start timer once on mount
        return () => {
            if (timerRef.current) clearInterval(timerRef.current); // cleanup on unmount
        };
    }, []);


  // ImageSlider.tsx (only the JSX inside the return changed)
  return (
    <div className="img-slider-frame">
      <section aria-label="Image Slider" style={{ width: "100%", height: "100%", position: "relative" }}>
        <a href="#after-image-slider-controls" className="skip-link">Skip Image Slider Controls</a>

        {/* Track wrapper: translate the whole strip, not each slide */}
        <div
          className="img-slider-track"
          style={{ transform: `translateX(-${100 * imageIndex}%)` }}
        >
          {images.map(({ url, alt }, index) => {
            const isVideo = /\.(mp4|webm)$/i.test(url);
            return isVideo ? (
              <video
                key={url}
                className="img-slider-media"
                src={url}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                aria-hidden={imageIndex !== index}
              />
            ) : (
              <img
                key={url}
                className="img-slider-media"
                src={url}
                alt={alt}
                aria-hidden={imageIndex !== index}
              />
            );
          })}
        </div>

        <button aria-label="View Previous Image" onClick={showPrevImage} className="img-slider-btn" style={{ left: 0 }}>
          <ArrowBigLeft />
        </button>
        <button aria-label="View Next Image" onClick={showNextImage} className="img-slider-btn" style={{ right: 0 }}>
          <ArrowBigRight />
        </button>

        <div style={{ position: "absolute", bottom: ".5rem", left: "50%", translate: "-50%", gap: ".25rem" }}>
          {images.map((_, index) => (
            <button
              key={index}
              className="img-slider-dot-btn"
              onClick={() => setImageIndex(index)}
              aria-label={`View Image ${index + 1}`}
            >
              {index === imageIndex ? <CircleDot aria-hidden /> : <Circle />}
            </button>
          ))}
        </div>
        <div id="after-image-slider-controls" />
      </section>
    </div>
  );

}